import { bProfile, ProfileRole, useProfile } from "@brg/auth";
import { API, Endpoints } from "@brg/api";
import { useCallback, useMemo } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useModal } from "core";
import { useToasts } from "@brg/ui";
import { CreateLinkTo } from "shared/constants/linkTo";
import { HsePolicies } from "shared/constants/HsePolicies";
import { downloadPDF } from "shared/components/PrintProject/model/downloadPDF";
import { isTeamChannel, ModerationType } from "../const/ModerationTypes";
import { isTeamJoinable, shouldShowTeamPublicActionsMenu } from "../helpers/shouldShowTeamActionsMenu";

type TeamActionsTeam = {
    teamId?: string;
    name?: string;
    route?: string;
    contextName?: string;
    moderationType?: number | null;
    isServiceTeam?: boolean;
    fieldOfActivity?: number | null;
    parentTeamId?: string | null;
    ownersIDs?: string[];
    membersIDs?: string[];
    pendingMemberIds?: string[];
    longreadEntityLink?: string;
    titleImages?: any[];
};

export const useTeamActionsMenu = (team?: TeamActionsTeam | null, options?: { isLanding?: boolean }) => {
    const profile = useProfile();
    const history = useHistory();
    const isTeamsRoute = !!useRouteMatch(['/teams/:teamName', '/teams/:teamName/brnd']);
    const modal = useModal();
    const toasts = useToasts();
    const { t, i18n } = useTranslation();
    const isLanding = !!options?.isLanding;

    const isChannel = useMemo(() => {
        const { isChannel } = isTeamChannel((team?.moderationType ?? 0) as ModerationType);
        return isChannel;
    }, [team?.moderationType]);

    const isSuperModer = useMemo(
        () => !!(profile && (bProfile.isModer(profile) || bProfile.isMiddleModer(profile))),
        [profile]
    );
    const isOwner = useMemo(
        () => !!(profile && team?.ownersIDs?.includes(profile.accountId)),
        [profile, team?.ownersIDs]
    );
    const isMember = useMemo(
        () => !!(profile && team?.membersIDs?.includes(profile.accountId)),
        [profile, team?.membersIDs]
    );
    const isPendingMember = useMemo(
        () => !!(profile && team?.pendingMemberIds?.includes(profile.accountId)),
        [profile, team?.pendingMemberIds]
    );

    const hasCurrentUnitAdminPolicy = useMemo(
        () =>
            !!(
                profile &&
                team?.teamId &&
                profile.rolesList &&
                Array.isArray(profile.rolesList) &&
                new ProfileRole(`${HsePolicies.admin_unit_prefix}${team.teamId}`).containsIn(profile.rolesList)
            ),
        [team?.teamId, profile]
    );
    const hasAllUnitsAdminPolicy = useMemo(
        () =>
            !!(
                profile &&
                profile.rolesList &&
                Array.isArray(profile.rolesList) &&
                new ProfileRole(HsePolicies.access_ui_units_admin).containsIn(profile.rolesList)
            ),
        [profile]
    );

    const canEdit = !!(isSuperModer || isOwner || hasAllUnitsAdminPolicy || hasCurrentUnitAdminPolicy);
    const showPublicActions = !!(profile && isTeamsRoute && shouldShowTeamPublicActionsMenu(team));
    const canShare = showPublicActions;
    const canReport = showPublicActions;
    const canJoin = !!(showPublicActions && isTeamJoinable(team) && !isOwner && !isMember && !isPendingMember);
    const canPrintPdf = !!(isLanding && canEdit);
    const isVisible = !!(profile && (canEdit || showPublicActions));

    const handleEdit = useCallback(() => {
        if (!team?.teamId) return;

        if (isLanding) {
            if (team.longreadEntityLink) {
                window.open(
                    `${team.longreadEntityLink.replace("project", "edit")}?shouldForcedLogin=true&saveLoginUrl=true`,
                    "_blank"
                );
                return;
            }
            const editUrl =
                team.moderationType === ModerationType.Outer
                    ? CreateLinkTo.teamExternalEdit(team.teamId)
                    : CreateLinkTo.teamEdit(team.teamId);
            history.push(`${editUrl}?tab=lg`);
            return;
        }

        if (team.moderationType === ModerationType.Outer) {
            history.push(CreateLinkTo.teamExternalEdit(team.teamId));
            return;
        }
        history.push(CreateLinkTo.teamEdit(team.teamId));
    }, [team, isLanding, history]);

    const handleShare = useCallback(() => {
        const shareUrl = `${window.location.origin}${window.location.pathname}`;
        modal.show("share", {
            customCopyUrl: shareUrl,
            title: team?.name || "",
            images: team?.titleImages || [],
        });
    }, [modal, team?.name, team?.titleImages]);

    const handleJoin = useCallback(() => {
        if (!team) return;
        modal.show("send-project-employ", {
            team,
        });
    }, [modal, team]);

    const handleReport = useCallback(() => {
        if (!team?.teamId) return;
        const organizationUrl = `${window.location.origin}${window.location.pathname}`;
        modal.show("report", {
            entityId: team.teamId,
            entityType: "team",
            additionalInfo: [team.name, organizationUrl].filter(Boolean).join("\n"),
            handleClose: () => {
                modal.hide();
            },
        });
    }, [modal, team?.teamId, team?.name]);

    const printPDFForm = useMemo(
        () => ({
            orientation: true,
            localization: i18n.language,
            teamId: team?.teamId,
            isPresentation: true,
        }),
        [team?.teamId, i18n.language]
    );

    const handlePrintPdf = useCallback(() => {
        toasts.info(`${t("print_project_toast")} ${isOwner ? t("print_project_toast") : team?.name} `);

        API.request()
            .endpoint(Endpoints.geograffee)
            .url("/api/Team/PrintPdf")
            .post()
            .body(printPDFForm)
            .send()
            .then((r) => r.json())
            .then((r) => {
                return r?.length > 0
                    ? downloadPDF(r, team?.name ?? "", toasts.error, toasts.info, t)
                    : console.error("no files");
            })
            .catch((e) => console.error(e));
    }, [printPDFForm, t, isOwner, team?.name, toasts]);

    return {
        isVisible,
        canEdit,
        canShare,
        canJoin,
        canReport,
        canPrintPdf,
        isChannel,
        handleEdit,
        handleShare,
        handleJoin,
        handleReport,
        handlePrintPdf,
    };
};

export type TeamActionsMenu = ReturnType<typeof useTeamActionsMenu>;
import { bProfile, ProfileRole, useProfile } from "@brg/auth";
import { API, Endpoints } from "@brg/api";
import { useCallback, useMemo } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useModal } from "core";
import { useToasts } from "@brg/ui";
import { CreateLinkTo } from "shared/constants/linkTo";
import { HsePolicies } from "shared/constants/HsePolicies";
import { downloadPDF } from "shared/components/PrintProject/model/downloadPDF";
import { isTeamChannel, ModerationType } from "../const/ModerationTypes";
import { isTeamJoinable, shouldShowTeamPublicActionsMenu } from "../helpers/shouldShowTeamActionsMenu";

type TeamActionsTeam = {
    teamId?: string;
    name?: string;
    route?: string;
    contextName?: string;
    moderationType?: number | null;
    isServiceTeam?: boolean;
    fieldOfActivity?: number | null;
    parentTeamId?: string | null;
    ownersIDs?: string[];
    membersIDs?: string[];
    pendingMemberIds?: string[];
    longreadEntityLink?: string;
    titleImages?: any[];
};

export const useTeamActionsMenu = (team?: TeamActionsTeam | null, options?: { isLanding?: boolean }) => {
    const profile = useProfile();
    const history = useHistory();
    const isTeamsRoute = !!useRouteMatch(['/teams/:teamName', '/teams/:teamName/brnd']);
    const modal = useModal();
    const toasts = useToasts();
    const { t, i18n } = useTranslation();
    const isLanding = !!options?.isLanding;

    const isChannel = useMemo(() => {
        const { isChannel } = isTeamChannel((team?.moderationType ?? 0) as ModerationType);
        return isChannel;
    }, [team?.moderationType]);

    const isSuperModer = useMemo(
        () => !!(profile && (bProfile.isModer(profile) || bProfile.isMiddleModer(profile))),
        [profile]
    );
    const isOwner = useMemo(
        () => !!(profile && team?.ownersIDs?.includes(profile.accountId)),
        [profile, team?.ownersIDs]
    );
    const isMember = useMemo(
        () => !!(profile && team?.membersIDs?.includes(profile.accountId)),
        [profile, team?.membersIDs]
    );
    const isPendingMember = useMemo(
        () => !!(profile && team?.pendingMemberIds?.includes(profile.accountId)),
        [profile, team?.pendingMemberIds]
    );

    const hasCurrentUnitAdminPolicy = useMemo(
        () =>
            !!(
                profile &&
                team?.teamId &&
                profile.rolesList &&
                Array.isArray(profile.rolesList) &&
                new ProfileRole(`${HsePolicies.admin_unit_prefix}${team.teamId}`).containsIn(profile.rolesList)
            ),
        [team?.teamId, profile]
    );
    const hasAllUnitsAdminPolicy = useMemo(
        () =>
            !!(
                profile &&
                profile.rolesList &&
                Array.isArray(profile.rolesList) &&
                new ProfileRole(HsePolicies.access_ui_units_admin).containsIn(profile.rolesList)
            ),
        [profile]
    );

    const canEdit = !!(isSuperModer || isOwner || hasAllUnitsAdminPolicy || hasCurrentUnitAdminPolicy);
    const showPublicActions = !!(profile && isTeamsRoute && shouldShowTeamPublicActionsMenu(team));
    const canShare = showPublicActions;
    const canReport = showPublicActions;
    const canJoin = !!(showPublicActions && isTeamJoinable(team) && !isOwner && !isMember && !isPendingMember);
    const canPrintPdf = !!(isLanding && canEdit);
    const isVisible = !!(profile && (canEdit || showPublicActions));

    const handleEdit = useCallback(() => {
        if (!team?.teamId) return;

        if (isLanding) {
            if (team.longreadEntityLink) {
                window.open(
                    `${team.longreadEntityLink.replace("project", "edit")}?shouldForcedLogin=true&saveLoginUrl=true`,
                    "_blank"
                );
                return;
            }
            const editUrl =
                team.moderationType === ModerationType.Outer
                    ? CreateLinkTo.teamExternalEdit(team.teamId)
                    : CreateLinkTo.teamEdit(team.teamId);
            history.push(`${editUrl}?tab=lg`);
            return;
        }

        if (team.moderationType === ModerationType.Outer) {
            history.push(CreateLinkTo.teamExternalEdit(team.teamId));
            return;
        }
        history.push(CreateLinkTo.teamEdit(team.teamId));
    }, [team, isLanding, history]);

    const handleShare = useCallback(() => {
        const shareUrl = `${window.location.origin}${window.location.pathname}`;
        modal.show("share", {
            customCopyUrl: shareUrl,
            title: team?.name || "",
            images: team?.titleImages || [],
        });
    }, [modal, team?.name, team?.titleImages]);

    const handleJoin = useCallback(() => {
        if (!team) return;
        modal.show("send-project-employ", {
            team,
        });
    }, [modal, team]);

    const handleReport = useCallback(() => {
        if (!team?.teamId) return;
        const organizationUrl = `${window.location.origin}${window.location.pathname}`;
        modal.show("report", {
            entityId: team.teamId,
            entityType: "team",
            additionalInfo: [team.name, organizationUrl].filter(Boolean).join("\n"),
            handleClose: () => {
                modal.hide();
            },
        });
    }, [modal, team?.teamId, team?.name]);

    const printPDFForm = useMemo(
        () => ({
            orientation: true,
            localization: i18n.language,
            teamId: team?.teamId,
            isPresentation: true,
        }),
        [team?.teamId, i18n.language]
    );

    const handlePrintPdf = useCallback(() => {
        toasts.info(`${t("print_project_toast")} ${isOwner ? t("print_project_toast") : team?.name} `);

        API.request()
            .endpoint(Endpoints.geograffee)
            .url("/api/Team/PrintPdf")
            .post()
            .body(printPDFForm)
            .send()
            .then((r) => r.json())
            .then((r) => {
                return r?.length > 0
                    ? downloadPDF(r, team?.name ?? "", toasts.error, toasts.info, t)
                    : console.error("no files");
            })
            .catch((e) => console.error(e));
    }, [printPDFForm, t, isOwner, team?.name, toasts]);

    return {
        isVisible,
        canEdit,
        canShare,
        canJoin,
        canReport,
        canPrintPdf,
        isChannel,
        handleEdit,
        handleShare,
        handleJoin,
        handleReport,
        handlePrintPdf,
    };
};

export type TeamActionsMenu = ReturnType<typeof useTeamActionsMenu>;
